import React, { useState } from 'react';
import { CreateUserData } from '../../services/user.service';
import { UserRole, AppType } from '../../types';

interface UserFormProps {
  onSubmit: (data: CreateUserData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({ onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState<CreateUserData>({
    username: '',
    password: '',
    role: UserRole.DOCTOR,
    allowedApps: [AppType.LEGACY, AppType.HAUSARZT, AppType.KINDERARZT],
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
          Benutzername *
        </label>
        <input
          type="text"
          id="username"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
          disabled={isLoading}
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Passwort *
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            disabled={isLoading}
            minLength={8}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Mindestens 8 Zeichen, 1 Großbuchstabe, 1 Kleinbuchstabe, 1 Zahl
        </p>
      </div>

      <div>
        <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
          Rolle *
        </label>
        <select
          id="role"
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
          disabled={isLoading}
        >
          <option value={UserRole.DOCTOR}>DOCTOR</option>
          <option value={UserRole.MANAGER}>MANAGER</option>
          <option value={UserRole.ADMIN}>ADMIN</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Erlaubte Bereiche *
        </label>
        <div className="space-y-2">
          {Object.values(AppType).map((app) => (
            <label key={app} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.allowedApps?.includes(app) || false}
                onChange={(e) => {
                  const current = formData.allowedApps || [];
                  if (e.target.checked) {
                    setFormData({ ...formData, allowedApps: [...current, app] });
                  } else {
                    setFormData({ ...formData, allowedApps: current.filter(a => a !== app) });
                  }
                }}
                disabled={isLoading}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                {app === AppType.LEGACY ? 'Legacy (Praxis Hayat)' :
                  app === AppType.HAUSARZT ? 'Hausarzt Medizin' :
                    'Kindermedizin'}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Wird erstellt...' : 'Benutzer erstellen'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:cursor-not-allowed"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
};

export default UserForm;
